import React, { useContext, useMemo, useState } from 'react'
import clsx from 'clsx'
import { GraphQL } from '@guestgoodie/graphql'
import * as Tooltip from '@radix-ui/react-tooltip'
import { HugeiconsIcon } from '@hugeicons/react'
import { HelpCircleIcon } from '@hugeicons/core-solid-rounded'

import {
  Controller,
  ControllerProps,
  ControllerRenderProps,
  FieldErrors,
  FieldPath,
  FieldValues,
  useFormContext,
  UseFormGetValues,
  UseFormSetValue,
  UseFormTrigger
} from 'react-hook-form'

import { tooltipContainer } from '@/utils/popovers'
import Checkbox from '@/components/ui/Checkbox'
import { Error, TranslationError } from './Error'
import { CustomContext } from './CustomProvider'

import {
  FieldTranslation,
  LocaleDropdown,
  useFieldTranslation,
  useTranslation
} from './Translation'

export type FieldChildrenProps<T extends FieldValues> = {
  errors: FieldErrors<T>
  trigger: UseFormTrigger<T>
  setValue: UseFormSetValue<T>
  getValues: UseFormGetValues<T>
  locale: GraphQL.AppLocale
  isDefaultLocale: boolean
  renderProps: ControllerRenderProps & {
    invalid: boolean
    readOnly: boolean
  }
}

type Props<T extends FieldValues, TName extends FieldPath<T> = FieldPath<T>> = {
  name: TName
  label?: string
  description?: string
  hint?: string
  size?: 'regular' | 'small'
  isInitialEnabled?: boolean
  canBeDisabled?: boolean
  rules?: ControllerProps<T, TName>['rules']
  disabled?: boolean
  resetOnUnmount?: boolean
  translationPrefix?: string
  onEnabledChange?: (isEnabled: boolean) => void
  children: (props: FieldChildrenProps<T>) => React.ReactElement
} & FieldValues

export function Field<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>
>(props: Props<T, TName>) {
  const {
    hasTranslations = false,
    isTranslatable,
    locales,
    defaultLocale
  } = useTranslation() ?? {}

  const shouldTranslate = useMemo(
    () => isTranslatable?.(props.name) ?? false,
    [props.name, isTranslatable]
  )

  if (!hasTranslations || !shouldTranslate || !locales || !defaultLocale) {
    return (
      <DefaultField
        {...props}
        baseName={props.name}
        locale={defaultLocale}
        isDefaultLocale
      />
    )
  }

  return (
    <FieldTranslation>
      <TranslatedField
        {...props}
        locales={locales}
        defaultLocale={defaultLocale}
      />
    </FieldTranslation>
  )
}

function TranslatedField<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>
>(
  props: Props<T, TName> & {
    defaultLocale: GraphQL.AppLocale
    locales: GraphQL.AppLocale[]
  }
) {
  const { defaultLocale, locales } = props
  const { locale } = useFieldTranslation() ?? {}

  return (
    <>
      {locales?.map(function (loc) {
        const translationKey = (
          props.translationPrefix
            ? `${props.translationPrefix}.translations.${loc}.${props.name}`
            : `translations.${loc}.${props.name}`
        ) as TName

        const name = defaultLocale === loc ? props.name : translationKey

        return (
          <div
            className={clsx(loc === locale ? 'block flex-1' : 'hidden')}
            key={name}
          >
            <DefaultField
              {...props}
              locale={locale}
              isDefaultLocale={defaultLocale === locale}
              name={name}
              baseName={props.name}
            />
          </div>
        )
      })}
    </>
  )
}

function DefaultField<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>
>(props: Props<T, TName> & { baseName: string }) {
  const {
    children,
    baseName,
    name,
    canBeDisabled = false,
    disabled = false,
    isInitialEnabled = true,
    label,
    description,
    rules,
    size = 'regular',
    locale,
    isDefaultLocale,
    resetOnUnmount = false,
    hint,
    onEnabledChange
  } = props

  const { control, trigger, setValue, getValues } = useFormContext<T>()
  const { readOnly, disabled: rootDisabled } = useContext(CustomContext)
  const [isEnabled, setIsEnabled] = useState(isInitialEnabled)

  return (
    <fieldset
      className={clsx(
        'flex min-w-0 flex-1 flex-col',
        size === 'regular' && 'gap-2.5',
        size === 'small' && 'gap-1'
      )}
    >
      <Controller<T, TName>
        name={name}
        disabled={disabled}
        control={control}
        rules={rules}
        shouldUnregister={resetOnUnmount}
        render={({
          field: { value, onChange, disabled, ...rest },
          formState: { errors }
        }) => {
          return (
            <>
              <Label
                name={name}
                baseName={baseName}
                canBeDisabled={canBeDisabled}
                isEnabled={isEnabled}
                label={label}
                hint={hint}
                size={size}
                onEnabled={isEnabled => {
                  setIsEnabled(isEnabled)
                  onEnabledChange?.(isEnabled)
                }}
              />
              {description && (
                <span className="text-regular text-text">{description}</span>
              )}
              <div className="flex flex-col gap-1">
                {isEnabled &&
                  children({
                    errors,
                    trigger,
                    setValue,
                    getValues,
                    locale,
                    isDefaultLocale,
                    renderProps: {
                      ...rest,
                      value,
                      onChange,
                      invalid: errors[name] ? true : false,
                      readOnly,
                      disabled: rootDisabled || disabled
                    }
                  })}
                <Error name={name} />
                <TranslationError name={baseName} locale={locale} />
              </div>
            </>
          )
        }}
      />
    </fieldset>
  )
}

interface LabelProps {
  label?: string
  baseName: string
  name: string
  isEnabled: boolean
  canBeDisabled: boolean
  size?: 'regular' | 'small'
  hint?: string
  onEnabled: (isEnabled: boolean) => void
}

function Label(props: LabelProps) {
  const {
    baseName,
    name,
    label,
    hint,
    isEnabled,
    canBeDisabled,
    onEnabled,
    size
  } = props

  return (
    <div className="flex flex-row items-center gap-2 justify-between">
      {canBeDisabled && (
        <Checkbox
          checked={isEnabled}
          onChange={() => onEnabled(!isEnabled)}
          className={clsx(
            'flex flex-row flex-wrap gap-1 text-text items-center',
            size === 'regular' && 'text-heading3',
            size === 'small' && 'text-regular font-bold'
          )}
        >
          {label}
          {hint && <HintTooltip text={hint} />}
        </Checkbox>
      )}
      {!canBeDisabled && (
        <label
          className={clsx(
            'flex flex-row flex-wrap gap-1 text-text items-center',
            size === 'regular' && 'text-heading3',
            size === 'small' && 'text-regular font-bold'
          )}
          htmlFor={name}
        >
          {label}
          {hint && <HintTooltip text={hint} />}
        </label>
      )}
      <LocaleDropdown name={baseName} />
    </div>
  )
}

function HintTooltip({ text }: { text: string }) {
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <button className="flex items-center rounded-full h-full" type="button">
          <HugeiconsIcon icon={HelpCircleIcon} className="size-5" />
        </button>
      </Tooltip.Trigger>
      <Tooltip.Portal container={tooltipContainer}>
        <Tooltip.Content
          collisionPadding={20}
          className="flex flex-col gap-1 rounded-small bg-black/70 p-2 text-white z-tooltip"
          style={{
            maxWidth: 'min(var(--radix-tooltip-content-available-width), 400px)'
          }}
          sideOffset={5}
        >
          <p className="text-regular">{text}</p>
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  )
}
